import { PopperPlacementType } from "@mui/base/Popper";
import { useHandler } from "@redotech/react-util/hook";
import type { AdvancedFilterData } from "@redotech/redo-model/views/views";
import { assertNever } from "@redotech/util/type";
import { memo, useRef, useState } from "react";
import type { AdvancedTableFilter } from "../advanced-filter";
import { planFilterCommit } from "./deferred-filter-commit";
import { FilterValueEditor } from "./filter-value-editor";

/**
 * The cascade-submenu content for one filter field in the deferred-pill
 * add-filter flow: renders the type-appropriate value editor (search +
 * checkboxes / calendar / list) anchored to the submenu, and commits the pill
 * on the first concrete value, updating it live afterward.
 *
 * This component is storage-model-agnostic: it never touches the caller's
 * filter list directly. Instead it surfaces the deferred-pill contract through
 * callbacks so it can be shared by callers with different storage shapes
 * (support-v2's `AdvancedFilterData[]` vs. table2's `AdvancedTableFilter[]`):
 *
 * - `onCommit(data)` fires once, when the pending filter first gets a concrete
 *   value, so the caller can append a new pill.
 * - `onUpdate(previous, next)` fires on every later edit. The caller must
 *   replace the pill whose stored data === `previous` BY REFERENCE IDENTITY,
 *   not by value. This is what keeps multi-select working without spawning
 *   duplicate pills, so `toTableFilter` MUST preserve the exact same
 *   `AdvancedFilterData` object reference on the returned filter's `.data`.
 */
export const FilterFieldSubmenu = memo(function FilterFieldSubmenu({
  initialData,
  toTableFilter,
  definitions,
  anchor,
  placement,
  onCommit,
  onUpdate,
  closeMenu,
}: {
  /** The default/empty filter data for the chosen field (already deep-copied). */
  initialData: AdvancedFilterData;
  /**
   * Resolve filter data into a full table filter for the value editor. MUST
   * preserve the SAME `data` object reference on the returned filter's `.data`
   * so committed-pill identity matching keeps working.
   */
  toTableFilter(data: AdvancedFilterData): AdvancedTableFilter | null;
  /** All filter definitions, forwarded to the value editor for cross-filter context. */
  definitions: AdvancedTableFilter[];
  anchor: HTMLElement | null;
  placement?: PopperPlacementType;
  /** First concrete value: the caller appends a new pill keeping this `data` reference. */
  onCommit(data: AdvancedFilterData): void;
  /** Later edits: the caller replaces the pill whose data === `previous`. */
  onUpdate(previous: AdvancedFilterData, next: AdvancedFilterData): void;
  /** The value editor was dismissed (clicked away). */
  closeMenu(): void;
}) {
  const [pendingData, setPendingData] =
    useState<AdvancedFilterData>(initialData);
  // Once committed as a pill, further edits update that same clause in place so
  // multi-select keeps working without spawning duplicate pills.
  const committedRef = useRef<AdvancedFilterData | null>(null);

  const handleValueChange = useHandler((tableFilter: AdvancedTableFilter) => {
    const data = tableFilter.data;
    setPendingData(data);
    const plan = planFilterCommit(committedRef.current, data);
    switch (plan.action) {
      case "add":
        onCommit(data);
        committedRef.current = plan.committed;
        break;
      case "update":
        onUpdate(plan.previous, data);
        committedRef.current = plan.committed;
        break;
      case "none":
        break;
      default:
        assertNever(plan);
    }
  });

  const tableFilter = toTableFilter(pendingData);
  if (!tableFilter) {
    return null;
  }
  return (
    <FilterValueEditor
      anchor={anchor}
      filter={tableFilter}
      filters={definitions}
      open
      placement={placement ?? "right-start"}
      setFilter={handleValueChange}
      setOpen={(open) => {
        // The value editor's own click-away (clicking outside it) closes the
        // whole add-filter menu. Selecting/ticking values does not.
        if (!open) {
          closeMenu();
        }
      }}
    />
  );
});
